import { useState, useEffect } from 'react';

/**
 * Custom hook to handle responsive layout adjustments
 * @param {number} mobileBreakpoint - Breakpoint for mobile layout (default: 768px)
 * @param {number} compactBreakpoint - Breakpoint for compact layout (default: 640px)
 * @returns {Object} - Object containing responsive state variables and helper functions
 */
const useResponsive = (mobileBreakpoint = 768, compactBreakpoint = 640) => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [isMobileView, setIsMobileView] = useState(false);
  const [isChartCompact, setIsChartCompact] = useState(false);

  // Effect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobileView(width < mobileBreakpoint);
      setIsChartCompact(width < compactBreakpoint);
    };
    
    handleResize(); // Initialize on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint, compactBreakpoint]);

  /**
   * Determine which models to show based on screen width and mobile state
   * @param {Array} modelColumns - Array of model objects 
   * @param {number} selectedModelIndex - Currently selected model index for mobile view
   * @returns {Array} - Array of model objects that should be displayed
   */
  const getVisibleModels = (modelColumns, selectedModelIndex) => {
    if (!isMobileView) {
      if (windowWidth < compactBreakpoint) {
        return modelColumns.slice(0, 2); // Show only first 2 on small mobile
      } else if (windowWidth < mobileBreakpoint) {
        return modelColumns.slice(0, 3); // Show first 3 on mobile
      } else if (windowWidth < 1024) {
        return modelColumns.slice(0, 5); // Show first 5 on tablet
      } else {
        return modelColumns; // Show all on desktop
      }
    } else {
      // In mobile view, show only the selected model
      return [modelColumns[selectedModelIndex]];
    }
  };

  return {
    windowWidth,
    isMobileView,
    isChartCompact,
    getVisibleModels
  };
};

export default useResponsive;
